import { marked } from 'marked';
import { useEffect, useRef } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { SectionErrorFallback } from '../../layout/ErrorBoundary/ErrorBoundary';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import java from 'highlight.js/lib/languages/java';
import css from 'highlight.js/lib/languages/css';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import sql from 'highlight.js/lib/languages/sql';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import markdown from 'highlight.js/lib/languages/markdown';
import plaintext from 'highlight.js/lib/languages/plaintext';
import protobuf from 'highlight.js/lib/languages/protobuf';
import go from 'highlight.js/lib/languages/go';
import rust from 'highlight.js/lib/languages/rust';
import graphql from 'highlight.js/lib/languages/graphql';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import diff from 'highlight.js/lib/languages/diff';
// Self-hosted theme (was a cdnjs CDN <link> in index.html). Bundling it via Vite
// keeps the token colors versioned with highlight.js and drops the third-party CSP entry.
import 'highlight.js/styles/tokyo-night-dark.css';
import InlineHighlight from "../InlineHighlight/InlineHighlight";
import { slugify } from "../../../utils/toc";
import { isExternalHref, renderHref } from "../../../utils/internalHref";
import { looseTableExtension } from "./looseTable";

interface MarkdownProps {
  content: string | null | undefined;
  maxSize?: string;
}

/**
 * Configure marked with options for better output
 */
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Recover the tables the deck-to-markdown conversion broke. Registered at module
// load, alongside the options, so the server render and the client render agree:
// a difference between the two is React #418 and a discarded page.
marked.use({ extensions: [looseTableExtension(renderHeaderlessTable)] });

/**
 * Register highlight.js languages once, at module load, so syntax highlighting is
 * applied at render time inside the `code` renderer below. Doing it here (rather
 * than in a client-only useEffect) means the highlighted token spans are emitted
 * during marked.parse identically on the server and the client — the SSR markup
 * already carries the colors (no post-hydration flash) and the hydration HTML
 * matches exactly, so there is no React hydration mismatch.
 */
const hljsLanguages: Record<string, any> = {
  javascript, js: javascript,
  typescript, ts: typescript,
  python, py: python,
  java,
  css,
  json,
  bash, shell: bash, sh: bash,
  sql,
  xml, html: xml,
  yaml, yml: yaml,
  markdown, md: markdown,
  protobuf, proto: protobuf,
  go, golang: go,
  rust, rs: rust,
  graphql, gql: graphql,
  dockerfile, docker: dockerfile,
  diff,
  text: plaintext, plaintext,
};
for (const [name, lang] of Object.entries(hljsLanguages)) {
  if (!hljs.getLanguage(name)) hljs.registerLanguage(name, lang);
}

/**
 * Helper function to escape HTML entities
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Neutralize dangerous URL schemes (javascript:, vbscript:, data: text/html) on
 * links/images. Runs inside the marked renderer, so it applies identically on the
 * server and the client — unlike a browser-only sanitizer, which would make the
 * SSR and hydration HTML diverge and trigger React hydration errors (#418).
 */
function safeUrl(href: string | null | undefined): string {
  if (!href) return '';
  const trimmed = href.trim();
  // Allow protocol-relative, root-relative, anchors, and normal http(s)/mailto.
  if (/^(?:https?:|mailto:|tel:|\/|#|\.)/i.test(trimmed)) return trimmed;
  // Block anything that resolves to an executable scheme.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return '';
  return trimmed;
}

// The chrome every table on the site wears, in one place.
//
// Two renderers draw tables: the GFM one below, and the loose-table extension
// for blocks that lost their delimiter row in a deck conversion (see
// ./looseTable.ts). They have to look identical, so the classes live here rather
// than being copied into both and drifting.
const TABLE_OPEN =
  '<div class="my-[2em] overflow-x-auto"><table class="w-full min-w-[36rem] border-collapse text-[17px] leading-[1.5] font-body text-brand-navy">';
const TABLE_CLOSE = '</table></div>';
const CELL_CLASS = 'px-4 py-3 align-top';
const ROW_CLASS = 'border-t border-border-subtle';

/**
 * A table with no header row.
 *
 * Used only by the loose-table extension, where the source carries no delimiter
 * row and therefore does not say which row, if any, is a header. Every row is a
 * body row: nothing is invented, and every cell still lands in its own column,
 * which is the thing that was broken.
 */
function renderHeaderlessTable(rows: string[][]): string {
  const body = rows
    .map(
      (row) =>
        `<tr class="${ROW_CLASS}">${row.map((cell) => `<td class="${CELL_CLASS} text-left">${cell}</td>`).join('')}</tr>`,
    )
    .join('');
  return `${TABLE_OPEN}<tbody>${body}</tbody>${TABLE_CLOSE}`;
}

/**
 * Custom renderer to match existing RichText styling
 * Updated for marked v5+ which passes token objects instead of strings
 */
const renderer: any = {
  heading(token: any) {
    const text = this.parser.parseInline(token.tokens);
    const level = token.depth;
    const headingClasses: Record<number, string> = {
      1: 'font-sans text-[44px] sm:text-[52px] font-bold mt-[1.8em] mb-[0.25em] first:mt-0 text-brand-navy leading-[1.15] tracking-[-0.022em]',
      2: 'font-sans text-[36px] sm:text-[42px] font-bold mt-[1.6em] mb-[0.25em] first:mt-0 text-brand-navy leading-[1.2] tracking-[-0.019em]',
      3: 'font-sans text-[32px] sm:text-[36px] font-bold mt-[1.4em] mb-[0.2em] first:mt-0 text-brand-navy leading-[1.25] tracking-[-0.016em]',
      4: 'font-sans text-[28px] sm:text-[32px] font-bold mt-[1.2em] mb-[0.2em] first:mt-0 text-brand-navy leading-[1.3] tracking-[-0.013em]',
      5: 'font-sans text-[24px] sm:text-[28px] font-bold mt-[1em] mb-[0.2em] first:mt-0 text-brand-navy leading-[1.35] tracking-[-0.01em]',
      6: 'font-sans text-[21px] sm:text-[24px] font-bold mt-[1em] mb-[0.2em] first:mt-0 text-brand-navy leading-[1.4] tracking-[-0.007em]',
    };
    const className = headingClasses[level] || headingClasses[6];
    // id + scroll-mt so the table-of-contents anchors land below the sticky nav.
    const id = slugify(token.text || "");
    const idAttr = id ? ` id="${id}"` : "";
    return `<h${level}${idAttr} class="scroll-mt-24 ${className}">${text}</h${level}>`;
  },

  paragraph(token: any) {
    const text = this.parser.parseInline(token.tokens);
    return `<p class="mb-[1.58em] mt-[1.58em] first:mt-0 text-brand-navy text-[21px] leading-[1.58] font-body tracking-[-0.003em] break-words">${text}</p>`;
  },

  list(token: any) {
    const ordered = token.ordered;
    let body = '';
    for (const item of token.items) {
      body += this.listitem(item);
    }
    const tag = ordered ? 'ol' : 'ul';
    const listClass = ordered
      ? 'list-decimal list-outside pl-8 sm:pl-10 mb-[1.58em] mt-[1.58em] space-y-[0.5em] text-brand-navy text-[21px] leading-[1.58] font-body ml-4'
      : 'list-disc list-outside pl-8 sm:pl-10 mb-[1.58em] mt-[1.58em] space-y-[0.5em] text-brand-navy text-[21px] leading-[1.58] font-body ml-4';
    return `<${tag} class="${listClass}">${body}</${tag}>`;
  },

  listitem(token: any) {
    const text = this.parser.parse(token.tokens);
    return `<li class="pl-2 leading-[1.58] marker:text-brand-navy/60">${text}</li>`;
  },

  blockquote(token: any) {
    const quote = this.parser.parse(token.tokens);
    return `<blockquote class="my-[2em] border-l-[3px] border-brand-navy/80 text-brand-navy/65 pl-[23px] pr-0 py-[3px] italic text-[21px] leading-[1.58] font-body">${quote}</blockquote>`;
  },

  link(token: any) {
    // renderHref collapses an absolute link back to gazar.dev into a path, and
    // isExternalHref then agrees with it. Deciding with href.startsWith('http')
    // meant 38 articles opened this site in a new tab. See utils/internalHref.
    const href = renderHref(safeUrl(token.href));
    const title = token.title;
    const text = this.parser.parseInline(token.tokens);
    const isExternal = isExternalHref(href);
    const relAttr = isExternal ? ' rel="noopener noreferrer"' : '';
    const targetAttr = isExternal ? ' target="_blank"' : '';
    return `<a href="${href}"${title ? ` title="${title}"` : ''}${relAttr}${targetAttr} class="text-inherit underline decoration-brand-navy/40 decoration-1 underline-offset-2 hover:text-brand-navy hover:decoration-brand-navy/80 transition-colors">${text}</a>`;
  },

  image(token: any) {
    const href = safeUrl(token.href);
    const title = token.title;
    const alt = token.text;
    return `<div class="my-10 flex justify-center"><img src="${href}"${alt ? ` alt="${alt}"` : ''}${title ? ` title="${title}"` : ''} loading="lazy" class="max-w-[3200px] w-full rounded-lg shadow-lg" style="aspect-ratio: 16/9; object-fit: cover;" /></div>`;
  },

  code(token: any) {
    const code = token.text;
    const lang = token.lang || 'text';

    if (lang === 'mermaid') {
      return `<div class="mermaid-container my-[2em] flex justify-center overflow-x-auto" data-mermaid="${encodeURIComponent(code)}"></div>`;
    }

    const langLabel = lang.charAt(0).toUpperCase() + lang.slice(1);
    // Highlight at render time when we have the grammar; fall back to plain
    // escaped text otherwise. hljs.highlight() returns already-escaped, span-
    // wrapped HTML, so it must not be passed back through escapeHtml.
    const grammar = hljs.getLanguage(lang);
    const highlighted = grammar
      ? hljs.highlight(code, { language: lang }).value
      : escapeHtml(code);
    const codeClass = grammar ? 'hljs ' : '';

    // The code travels in a `data-` attribute and the handler is bound in the effect
    // below, rather than being written as an inline `onclick`.
    //
    // The inline version was live for months and broke every code block containing an
    // apostrophe, which on a site about code is most of them. It read
    // `decodeURIComponent('${encodeURIComponent(code)}')`, and **encodeURIComponent does
    // not escape `'`**, so a snippet containing `// Don't` emitted `…'%2F%2F Don't…'`,
    // closed the JS string early and threw `missing ) after argument list`. GlitchTip 151
    // and 153 caught it; on `/algorithm/algorithms-worth-knowing-in-code-review` three of
    // the first five buttons on the page were dead.
    //
    // Escaping the apostrophe as well would have fixed those three buttons and left the
    // real fault in place, which is **building JavaScript source inside an HTML attribute
    // by string concatenation**. That returns the next time the snippet contains something
    // the encoder happens not to escape. A `data-` attribute has no such failure mode:
    // percent-encoded output is plain ASCII with no quotes or angle brackets in it, so
    // there is nothing left to terminate early.
    return `
      <div class="my-[2em] bg-[#1e1e1e] rounded-lg overflow-hidden shadow-lg border border-[rgba(255,255,255,0.1)]">
        <div class="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-[rgba(255,255,255,0.1)]">
          <span class="text-[13px] font-mono text-[rgba(255,255,255,0.6)] uppercase tracking-wider">${langLabel}</span>
          <button
            type="button"
            data-copy-code="${encodeURIComponent(code)}"
            class="text-[13px] text-[rgba(255,255,255,0.6)] hover:text-[rgba(255,255,255,0.9)] transition-colors px-2 py-1 rounded hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
          >
            Copy
          </button>
        </div>
        <div class="overflow-x-auto bg-[#1e1e1e]">
          <pre class="m-0 p-5 bg-[#1e1e1e]"><code class="${codeClass}language-${lang} text-[14px] leading-[1.6] font-mono block">${highlighted}</code></pre>
        </div>
      </div>
    `;
  },

  codespan(token: any) {
    const code = escapeHtml(token.text);
    return `<code class="px-[6px] py-[3px] bg-brand-navy/8 rounded text-[18px] font-mono text-brand-red border border-brand-navy/10">${code}</code>`;
  },

  hr() {
    return '<hr class="my-[2.5em] border-t border-brand-navy/15" />';
  },

  // GFM tables had no renderer at all, so they fell through to marked's bare
  // <table> — and Preflight removes borders/spacing, leaving columns to collide.
  // Body text is 21px; that's too wide for tabular data, so cells step down to
  // 17px. The wrapper scrolls rather than letting narrow viewports crush columns.
  table(token: any) {
    const alignClass = (a: string | null) =>
      a === 'center' ? 'text-center' : a === 'right' ? 'text-right' : 'text-left';
    const head = token.header
      .map(
        (cell: any, i: number) =>
          `<th class="px-4 py-3 font-heading text-[15px] font-bold uppercase tracking-wider text-brand-navy ${alignClass(token.align[i])}">${this.parser.parseInline(cell.tokens)}</th>`,
      )
      .join('');
    const body = token.rows
      .map(
        (row: any[]) =>
          `<tr class="${ROW_CLASS}">${row
            .map(
              (cell: any, i: number) =>
                `<td class="${CELL_CLASS} ${alignClass(token.align[i])}">${this.parser.parseInline(cell.tokens)}</td>`,
            )
            .join('')}</tr>`,
      )
      .join('');
    return `${TABLE_OPEN}<thead class="border-b-2 border-brand-navy/25 bg-brand-navy/[0.03]"><tr>${head}</tr></thead><tbody>${body}</tbody>${TABLE_CLOSE}`;
  },

  strong(token: any) {
    const text = this.parser.parseInline(token.tokens);
    return `<strong class="font-bold text-inherit">${text}</strong>`;
  },

  em(token: any) {
    const text = this.parser.parseInline(token.tokens);
    return `<em class="italic text-inherit">${text}</em>`;
  },
};

// Set custom renderer
marked.use({ renderer });

let mermaidCounter = 0;
let mermaidInitialized = false;

const Markdown = ({ content, maxSize = "4xl" }: MarkdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Copy buttons. One delegated listener on the container rather than a handler per
  // button, so it keeps working when the markdown re-renders and there is nothing to
  // unbind per block. Separate from the mermaid effect below because it must not be
  // caught up in that one's retry/cancel machinery: copying has no async work to wait on.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const onClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest('[data-copy-code]');
      if (!(target instanceof HTMLElement)) return;

      const encoded = target.dataset.copyCode;
      if (encoded === undefined) return;

      // Restored on the timer below. Read before the await so a second click during the
      // "Copied!" window cannot latch that as the resting label.
      const restore = target.dataset.copyLabel ?? 'Copy';
      target.dataset.copyLabel = restore;

      navigator.clipboard
        .writeText(decodeURIComponent(encoded))
        .then(() => {
          target.textContent = 'Copied!';
        })
        .catch(() => {
          // Clipboard access is refused outside a secure context and in some embedded
          // browsers. Say so rather than showing "Copied!" over an empty clipboard,
          // which is the one outcome worse than the button plainly not working.
          target.textContent = 'Press Ctrl+C';
        })
        .finally(() => {
          window.setTimeout(() => {
            target.textContent = restore;
          }, 2000);
        });
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [content]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Syntax highlighting is applied at render time (see the `code` renderer and
    // the module-level language registration above), so the effect only has to
    // render mermaid diagrams.
    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout>;
    let attempts = 0;

    // A render pass. Retried a few times so it survives hydration/mount timing
    // where the ref or the injected container is not ready on the first tick
    // (the single-shot version silently missed and never recovered).
    const pass = async (): Promise<boolean> => {
      const root = containerRef.current;
      if (!root) return false;
      {
        const containers = root.querySelectorAll('[data-mermaid]:not([data-processed])');
        if (containers.length > 0) {
          // Only pull in mermaid (and its cytoscape/katex deps, ~1 MB) when a page
          // actually contains a diagram. Vite splits this into an on-demand chunk.
          // The dynamic import can reject if the chunk 404s after a redeploy (a
          // client holding stale HTML requests a hash that no longer exists) or on
          // a network blip. Swallow it: the diagram just stays unrendered instead
          // of throwing an unhandled rejection into GlitchTip (#110).
          let mermaid: typeof import('mermaid').default;
          try {
            mermaid = (await import('mermaid')).default;
          } catch (e) {
            console.error('Mermaid chunk failed to load:', e);
            return false;
          }
          if (!mermaidInitialized) {
            // House mermaid style, matches the brand look used across gazar.dev
            // (navy ink on light sky/green/amber tints, hand-drawn). Keep in sync
            // with the hub deck engine's theme.
            try {
            mermaid.initialize({
              startOnLoad: false,
              theme: 'base',
              look: 'handDrawn',
              securityLevel: 'loose',
              flowchart: { curve: 'basis', htmlLabels: true, padding: 14, useMaxWidth: true },
              themeVariables: {
                fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif',
                fontSize: '14px',
                primaryColor: '#DCEBFE',
                primaryTextColor: '#09244B',
                primaryBorderColor: '#09244B',
                lineColor: '#09244B',
                secondaryColor: '#D6F5E3',
                tertiaryColor: '#FEF3C7',
                noteBkgColor: '#FEF3C7',
                noteTextColor: '#09244B',
                noteBorderColor: '#B45309',
                clusterBkg: '#FBFAF7',
                clusterBorder: '#09244B',
                edgeLabelBackground: '#FBFAF7',
                mainBkg: '#DCEBFE',
                actorBkg: '#DCEBFE',
                actorBorder: '#09244B',
                actorTextColor: '#09244B',
                signalColor: '#09244B',
                signalTextColor: '#09244B',
              },
            });
            mermaidInitialized = true;
            } catch (e) { console.error('Mermaid init error:', e); }
          }
          for (const container of Array.from(containers)) {
            const encoded = container.getAttribute('data-mermaid');
            if (!encoded) continue;
            try {
              const source = decodeURIComponent(encoded);
              const id = `mermaid-${mermaidCounter++}`;
              const { svg } = await mermaid.render(id, source);
              container.innerHTML = svg;
              container.setAttribute('data-processed', 'true');
            } catch (e) {
              console.error('Mermaid render error:', e);
            }
          }
        }
      }
      return root.querySelectorAll('[data-mermaid]:not([data-processed])').length === 0;
    };

    const tick = async () => {
      if (cancelled) return;
      const done = await pass();
      attempts += 1;
      if (!done && !cancelled && attempts < 6) timerId = setTimeout(tick, 300);
    };
    timerId = setTimeout(tick, 0);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [content]);

  // Ensure content is a string
  let contentString: string;
  if (typeof content === 'string') {
    contentString = content;
  } else if (content == null) {
    return null;
  } else {
    // If it's an object, return null and let RichText handle it
    console.warn('Markdown component received non-string content:', typeof content, content);
    return null;
  }

  if (!contentString || !contentString.trim()) {
    return null;
  }

  // Convert markdown to HTML. This must produce the *same* string on the server
  // and the client: `marked` is deterministic and the custom renderer above
  // neutralizes dangerous URL schemes, so the SSR markup and the hydration markup
  // match exactly. (A browser-only DOMPurify pass used to run here, which made the
  // client re-serialize the HTML differently from the server and threw React
  // hydration errors #418 on every article/review/about page. It also never
  // protected the SSR payload — the raw HTML was already streamed to the browser —
  // so removing it costs no real protection for this trusted, admin-authored CMS
  // content, while the renderer-level URL guard keeps the actual injection vector
  // closed on both sides.)
  const html = marked.parse(contentString.trim(), { async: false });

  // Apply max width class (matching RichText component)
  const maxWidthClass = maxSize === "none" ? "" : maxSize === "4xl" ? "" : `max-w-${maxSize}`;

  return (
    <>
      <style>{`
        .Markdown pre code {
          color: #d4d4d4;
          display: block;
          overflow-x: auto;
        }
        .Markdown pre code.hljs {
          background: transparent !important;
          padding: 0 !important;
        }
        /* Cells inherit word-break/word-wrap: break-word from body (tailwind.css).
           That makes a column's min-content width one character, so auto layout
           squeezes columns and splits words mid-syllable ("Communi/cation").
           Wrap at spaces instead — the wrapper above scrolls when space runs out. */
        .Markdown table th,
        .Markdown table td {
          word-break: normal;
          overflow-wrap: normal;
        }
      `}</style>
      <div
        ref={containerRef}
        className={`Markdown ${maxWidthClass} mx-auto px-4`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
};

export default withErrorBoundary(Markdown, { FallbackComponent: SectionErrorFallback });
